import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import rehypeRaw from 'rehype-raw';
import 'katex/dist/katex.min.css';
import DOMPurify from 'dompurify';
import './Loader.css';
import { MessageCircle, RotateCw, Brain, Book, Layout, FileText, Zap, BookOpen, Network, Search, Highlighter, Underline, Bold, Copy, BookmarkPlus, MessageSquare, Share2, CheckSquare, Download, ChevronUp, ChevronDown } from 'lucide-react';
import Quiz from './Quiz';
import Flashcards from './Flashcards';
import ConceptMap from './ConceptMap';
import '../styles/AITools.css';
import { toast } from 'react-hot-toast';
import { visit } from 'unist-util-visit';
import { unified } from 'unified';
import remarkParse from 'remark-parse';
import remarkGfm from 'remark-gfm';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import ShareModal from './ShareModal';
import Loader from './Loader';
import { useTheme } from '../contexts/ThemeContext';
import LectureVisualization from './LectureVisualization';
import html2pdf from 'html2pdf.js';
import ReactDOM from 'react-dom/client';
import { jsPDF } from 'jspdf';
import canvg from 'canvg';
import ExportOptionsModal from './ExportOptionsModal';
import { exportToPDF } from '../utils/pdfExport';

const latexStyles = `
.prose {
    height: 100%;
    overflow-y: auto;
    contain: strict;
}

.katex-display {
    overflow-x: auto;
    overflow-y: hidden;
    max-width: 100%;
    padding: 0.5em 0;
    margin: 0.5em 0;
}

.katex-display > .katex {
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
}

.katex-html {
    max-width: 100%;
    overflow-x: auto;
    overflow-y: hidden;
}
`;

const pdfStyles = `
.pdf-export {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
    color: #000;
    line-height: 1.6;
    padding: 20px;
    max-width: 800px;
    margin: 0 auto;
}

.pdf-export h1 {
    font-size: 24px;
    font-weight: bold;
    margin-bottom: 16px;
    color: #000;
}

.pdf-export h2 {
    font-size: 20px;
    font-weight: bold;
    margin-top: 24px;
    margin-bottom: 12px;
    color: #000;
}

.pdf-export h3 {
    font-size: 18px;
    font-weight: bold;
    margin-top: 20px;
    margin-bottom: 10px;
    color: #000;
}

.pdf-export p {
    margin-bottom: 12px;
    color: #000;
}

.pdf-export code {
    background-color: #f5f5f5;
    padding: 2px 4px;
    border-radius: 4px;
    font-family: monospace;
}

.pdf-export pre {
    background-color: #f5f5f5;
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    margin: 12px 0;
}

.pdf-export ul, .pdf-export ol {
    margin: 12px 0;
    padding-left: 24px;
}

.pdf-export li {
    margin-bottom: 6px;
}

.pdf-export img {
    max-width: 100%;
    height: auto;
    margin: 16px 0;
}

.pdf-export blockquote {
    border-left: 4px solid #e0e0e0;
    margin: 16px 0;
    padding-left: 16px;
    font-style: italic;
}

.pdf-export table {
    border-collapse: collapse;
    width: 100%;
    margin: 16px 0;
}

.pdf-export th, .pdf-export td {
    border: 1px solid #e0e0e0;
    padding: 8px;
    text-align: left;
}

.pdf-export th {
    background-color: #f5f5f5;
}
`;

const LectureDetail = () => {
    const { theme } = useTheme();
    const [activeTab, setActiveTab] = useState('notes');
    const [lecture, setLecture] = useState(null);
    const [processedTranscript, setProcessedTranscript] = useState(null);
    const [error, setError] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [needsProcessing, setNeedsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [partialResult, setPartialResult] = useState(null);
    const { id, lectureId, shareToken } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const className = location.state?.className;
    const [activeHeading, setActiveHeading] = useState(null);
    const [mobileToC, setMobileToC] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    // Use useRef to prevent duplicate processing
    const hasProcessedTranscript = useRef(false);
    
    // Add a ref to track ToC initialization state
    const tocInitialized = useRef(false);
    const scrollHandlerActive = useRef(false);

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedText, setSelectedText] = useState('');
    const [highlights, setHighlights] = useState([]);
    const [underlines, setUnderlines] = useState([]);
    const [bookmarks, setBookmarks] = useState([]);
    const [searchResults, setSearchResults] = useState([]);
    const [activeAITool, setActiveAITool] = useState(null);

    const [quiz, setQuiz] = useState(null);
    const [flashcards, setFlashcards] = useState(null);
    const [conceptMap, setConceptMap] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generationError, setGenerationError] = useState(null);
    const [isAdding, setIsAdding] = useState(false);

    const [readingProgress, setReadingProgress] = useState(0);
    const contentRef = useRef(null);

    // Add new ref for TOC
    const tocRef = useRef(null);

    // Add new ref for the prose container
    const proseRef = useRef(null);

    const { isLoaded, isSignedIn } = useAuth();
    const authenticatedFetch = useAuthenticatedFetch();

    const [showShareModal, setShowShareModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);

    // Add mounted ref
    const isMounted = useRef(false);

    // Add this near the top of the component with other state declarations
    const [contentReady, setContentReady] = useState(false);

    // Update this effect to check when content is ready
    useLayoutEffect(() => {
        // Simpler approach: just set contentReady to true when content is available
        if (processedTranscript?.content) {
            setContentReady(true);
        }
    }, [processedTranscript?.content]);

    // Add an effect to initialize reading progress when content loads
    useEffect(() => {
        if (activeTab === 'notes' && contentRef.current && processedTranscript?.content) {
            // Calculate initial progress after a short delay to ensure content has rendered
            const timer = setTimeout(() => {
                const scrollContainer = contentRef.current;
                if (!scrollContainer) return;
                
                const scrollTop = scrollContainer.scrollTop;
                const scrollHeight = scrollContainer.scrollHeight;
                const clientHeight = scrollContainer.clientHeight;
                
                if (scrollHeight <= clientHeight) {
                    setReadingProgress(100); // Content fits without scrolling
                } else {
                    const progress = Math.min(100, Math.max(0, (scrollTop / (scrollHeight - clientHeight)) * 100));
                    setReadingProgress(Math.round(progress));
                }
            }, 1000);
            
            return () => clearTimeout(timer);
        }
    }, [activeTab, processedTranscript?.content]);

    // Helper function to check if content needs to be generated
    const needsGeneration = (content) => {
        return !content || Object.keys(content).length === 0;
    };

    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (query.trim()) {
            // Case-insensitive search
            const results = lecture.transcript.split('\n')
                .map((line, index) => ({
                    line,
                    index,
                    matches: line.toLowerCase().includes(query.toLowerCase())
                }))
                .filter(item => item.matches);
            setSearchResults(results);
        } else {
            setSearchResults([]);
        }
    };

    const handleTextSelection = () => {
        const selection = window.getSelection();
        const text = selection.toString().trim();
        if (text) {
            setSelectedText(text);
        }
    };

    const handleHighlight = () => {
        if (selectedText) {
            const newText = selectedText;
            setHighlights(prev => [...prev, newText]);
            setSelectedText('');
            window.getSelection().removeAllRanges(); // Clear selection
        }
    };

    const handleUnderline = () => {
        if (selectedText) {
            const newText = selectedText;
            setUnderlines(prev => [...prev, newText]);
            setSelectedText('');
            window.getSelection().removeAllRanges(); // Clear selection
        }
    };

    const handleCopy = async () => {
        if (selectedText) {
            try {
                await navigator.clipboard.writeText(selectedText);
                toast.success('Copied to clipboard');
                setSelectedText('');
                window.getSelection().removeAllRanges();
            } catch (err) {
                toast.error('Failed to copy text');
            }
        }
    };

    const handleBookmark = () => {
        if (selectedText) {
            const newBookmark = {
                text: selectedText,
                timestamp: new Date().toISOString()
            };
            setBookmarks(prev => [...prev, newBookmark]);
            setSelectedText('');
            window.getSelection().removeAllRanges();
            toast.success('Bookmark added');
        }
    };

    // Add this effect to handle the loading progress animation
    useEffect(() => {
        let progressInterval;
        if (isLoading && loadingProgress < 90) {
            progressInterval = setInterval(() => {
                setLoadingProgress(prev => {
                    const increment = Math.max(1, Math.floor((90 - prev) / 5));
                    return Math.min(90, prev + increment);
                });
            }, 500);
        }
        return () => clearInterval(progressInterval);
    }, [isLoading, loadingProgress]);

    // Add function to handle adding shared lecture to account
    const handleAddToAccount = async () => {
        if (!shareToken) return;

        setIsAdding(true);
        try {
            const response = await authenticatedFetch(
                `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${shareToken}`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({ shareToken })
                }
            );

            if (response.ok) {
                const data = await response.json();
                toast.success('Lecture added to your account');
                navigate(`/class/${data.classId}/lecture/${data.id}`);
            } else {
                const error = await response.json();
                toast.error(error.message || 'Failed to add lecture');
            }
        } catch (error) {
            console.error('Error adding lecture:', error);
            toast.error('Failed to add lecture');
        } finally {
            setIsAdding(false);
        }
    };

    // Update the fetch lecture effect to handle share token
    useEffect(() => {
        let isMounted = true;

        const fetchLecture = async () => {
            if (!isLoaded || !isSignedIn) return;

            try {
                const url = shareToken
                    ? `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/share/${shareToken}/view`
                    : `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/lectures/${id}/${lectureId}`;

                const response = await authenticatedFetch(url);

                if (!response.ok) {
                    throw new Error('Failed to fetch lecture');
                }

                const data = await response.json();

                if (isMounted) {
                    setLecture(data);
                    if (data.processed_transcript) {
                        try {
                            const processed = typeof data.processed_transcript === 'string'
                                ? JSON.parse(data.processed_transcript)
                                : data.processed_transcript;
                            setProcessedTranscript(processed);
                        } catch (e) {
                            console.error('Error parsing processed_transcript:', e);
                        }
                    } else if (data.transcript && !shareToken) {
                        // Auto-trigger processing if the lecture has a transcript but not processed_transcript
                        console.log('Lecture needs processing, will auto-trigger processing');
                        setNeedsProcessing(true);
                    }
                }
            } catch (error) {
                if (isMounted) {
                    console.error('Error fetching lecture:', error);
                    setError('Failed to load lecture');
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchLecture();

        return () => {
            isMounted = false;
        };
    }, [id, lectureId, shareToken, isLoaded, isSignedIn]);

    // Add a useEffect to auto-trigger processing when needed
    useEffect(() => {
        if (needsProcessing && lecture?.transcript && !isProcessing && !shareToken) {
            console.log('Auto-triggering lecture processing');
            // Auto-trigger processing
            const autoProcess = async () => {
                try {
                    setIsProcessing(true);
                    await processTranscript(lecture.transcript, lecture.topic || lecture.title || '');
                    setNeedsProcessing(false);
                } catch (error) {
                    console.error('Auto-processing failed:', error);
                }
            };
            
            autoProcess();
        }
    }, [needsProcessing, lecture, isProcessing, shareToken]);

    // Update processTranscript function to use authenticatedFetch
    const processTranscript = async (transcript, topic) => {
        try {
            setProcessingProgress(0);
            const response = await authenticatedFetch(
                'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/process-transcript',
                {
                    method: 'POST',
                    body: JSON.stringify({ 
                        transcript, 
                        topic,
                        classId: id,
                        lectureId
                    }),
                }
            );
            if (!response.ok) {
                throw new Error('Failed to process transcript');
            }

            // Read the SSE stream manually
            const reader = response.body.getReader();
            const decoder = new TextDecoder('utf-8');
            let buffer = '';
            let completedResult = null;

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                // Decode the chunk and add it to our buffer
                buffer += decoder.decode(value, { stream: true });

                // Process complete messages from the buffer
                const lines = buffer.split('\n');
                buffer = lines.pop() || ''; // Keep the last incomplete line in the buffer

                for (const line of lines) {
                    if (line.trim() === '' || line.startsWith(':')) continue; // Skip empty lines and comments

                    if (line.startsWith('data: ')) {
                        try {
                            const data = JSON.parse(line.slice(6)); // Remove 'data: ' prefix

                            switch (data.status) {
                                case 'started':
                                    setProcessingProgress(5);
                                    break;
                                case 'first_pass_complete':
                                    setProcessingProgress(30);
                                    if (data.partial_result) {
                                        setPartialResult(data.partial_result);
                                    }
                                    break;
                                case 'generating_visualizations':
                                    const visualizationProgress = 30 + (data.progress * 0.6);
                                    setProcessingProgress(Math.round(visualizationProgress));
                                    break;
                                case 'completed':
                                    setProcessingProgress(100);
                                    completedResult = data.result;
                                    break;
                                case 'error':
                                    throw new Error(data.error || 'Unknown error occurred');
                            }
                        } catch (err) {
                            console.error('Error parsing SSE data:', err, 'Line:', line);
                            continue; // Skip this line if we can't parse it
                        }
                    }
                }
            }

            if (!completedResult) {
                throw new Error('No completed result received from transcript processing');
            }

            setProcessingProgress(100);
            // Update both the processed transcript and the lecture data
            setProcessedTranscript(completedResult);
            setLecture(prev => ({
                ...prev,
                title: completedResult.title,
                processed_transcript: completedResult,
            }));

            await saveLecture(completedResult);
            return completedResult;
        } catch (error) {
            console.error('Error processing transcript:', error);
            setError('Failed to process the lecture transcript. Please try again later.');
            throw error;
        } finally {
            setIsProcessing(false);
            setProcessingProgress(0);
        }
    };

    // Update saveLecture function to use authenticated fetch
    const saveLecture = async (processedData) => {
        try {
            const response = await authenticatedFetch(
                `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/update-lecture/${id}/${lectureId}`,
                {
                    method: 'PUT',
                    body: JSON.stringify({
                        processedTranscript: processedData,
                        status: 'processed',
                    })
                }
            );

            if (!response.ok) {
                throw new Error('Failed to save processed lecture');
            }
        } catch (error) {
            console.error('Error saving processed lecture:', error);
            toast.error('Failed to save lecture');
        }
    };

    const resetLectureStatus = async () => {
        try {
            const response = await authenticatedFetch(
                `https://classify-alpha-a1e5331e0be0.herokuapp.com/api/update-lecture/${id}/${lectureId}`,
                {
                    method: 'PUT',
                    body: JSON.stringify({
                        status: 'pending'
                    })
                }
            );

            if (!response.ok) {
                throw new Error('Failed to reset lecture status');
            }
        } catch (error) {
            console.error('Error resetting lecture status:', error);
            throw error;
        }
    };

    const handleRegenerate = async () => {
        if (isProcessing) return;
        setIsProcessing(true);
        hasProcessedTranscript.current = true;
        try {
            await resetLectureStatus();
            await processTranscript(lecture.transcript, lecture.title);
            toast.success('Lecture notes regenerated successfully');
        } catch (error) {
            console.error('Error regenerating transcript:', error);
            toast.error('Failed to regenerate lecture notes');
        } finally {
            setIsProcessing(false);
        }
    };

    // Modify the AI generation functions to use authenticated fetch
    const generateFlashcards = async () => {
        if (!needsGeneration(flashcards)) return;

        setIsGenerating(prev => ({ ...prev, flashcards: true }));
        setGenerationError(null);
        try {
            const response = await authenticatedFetch(
                'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/generate-flashcards',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        transcript: lecture.transcript.trim(),
                        topic: lecture.title,
                        lectureId,
                        classId: id
                    })
                }
            );

            const data = await response.json();
            setFlashcards(data);
        } catch (error) {
            console.error('Error generating flashcards:', error);
            setGenerationError(error.message);
        }
        setIsGenerating(prev => ({ ...prev, flashcards: false }));
    };

    const generateConceptMap = async () => {
        if (!needsGeneration(conceptMap)) return;

        setIsGenerating(prev => ({ ...prev, conceptMap: true }));
        setGenerationError(null);
        try {
            console.log('Generating concept map...');
            const response = await authenticatedFetch(
                'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/generate-concept-map',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        transcript: lecture.transcript.trim(),
                        topic: lecture.title,
                        lectureId,
                        classId: id
                    })
                }
            );

            const data = await response.json();
            console.log('Received concept map data:', data);

            // Validate concept map structure
            if (!data.nodes || !data.edges) {
                throw new Error('Invalid concept map structure received from server');
            }

            setConceptMap(data);
        } catch (error) {
            console.error('Error generating concept map:', error);
            setGenerationError(error.message);
        } finally {
            setIsGenerating(prev => ({ ...prev, conceptMap: false }));
        }
    };

    const generateQuiz = async () => {
        if (!needsGeneration(quiz)) return;

        setIsGenerating(prev => ({ ...prev, quiz: true }));
        setGenerationError(null);
        try {
            const response = await authenticatedFetch(
                'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/generate-quiz',
                {
                    method: 'POST',
                    body: JSON.stringify({
                        transcript: lecture.transcript.trim(),
                        topic: lecture.title,
                        lectureId,
                        classId: id
                    })
                }
            );

            const data = await response.json();
            setQuiz(data);
        } catch (error) {
            console.error('Error generating quiz:', error);
            setGenerationError(error.message);
        }
        setIsGenerating(prev => ({ ...prev, quiz: false }));
    };

    // Update useEffect to handle initial data
    useEffect(() => {
        if (lecture?.quiz) setQuiz(lecture.quiz);
        if (lecture?.flashcards) setFlashcards(lecture.flashcards);
        if (lecture?.concept_map) setConceptMap(lecture.concept_map);
    }, [lecture]);

    // Update useEffect for AI tools
    useEffect(() => {
        if (activeTab === 'ai' && lecture?.transcript) {
            switch (activeAITool) {
                case 'quiz':
                    if (needsGeneration(quiz)) generateQuiz();
                    break;
                case 'flashcards':
                    if (needsGeneration(flashcards)) generateFlashcards();
                    break;
                case 'concept-map':
                    if (needsGeneration(conceptMap)) generateConceptMap();
                    break;
            }
        }
    }, [activeTab, activeAITool, lecture]);

    // Replace the existing scroll handling logic with this improved version
    useEffect(() => {
        // Only run this if we're on the notes tab with content
        if (activeTab !== 'notes' || !contentRef.current || !processedTranscript?.content) {
            return;
        }
        
        // Don't re-initialize if already done
        if (tocInitialized.current) {
            return;
        }
        
        // Mark as initialized to prevent multiple setups
        tocInitialized.current = true;
        
        // Wait for content to be fully rendered
        const setupTimer = setTimeout(() => {
            const scrollContainer = contentRef.current;
            if (!scrollContainer) {
                return;
            }
            
            // Get all headings once
            const headings = proseRef.current ? Array.from(proseRef.current.querySelectorAll('h1[id], h2[id], h3[id]')) : [];
            
            if (headings.length > 0) {
                // Set initial heading silently without triggering scroll updates
                setActiveHeading(headings[0].id);
                
                // Define scroll handler function
                function handleScroll() {
                    if (!scrollContainer || !scrollHandlerActive.current) return;
                    
                    // Calculate scroll progress
                    const scrollTop = scrollContainer.scrollTop;
                    const scrollHeight = scrollContainer.scrollHeight;
                    const clientHeight = scrollContainer.clientHeight;
                    
                    const maxScroll = scrollHeight - clientHeight;
                    const progress = maxScroll <= 0 ? 100 : Math.min(100, Math.max(0, (scrollTop / maxScroll) * 100));
                    const roundedProgress = Math.round(progress);
                    
                    // Update progress bar
                    setReadingProgress(roundedProgress);
                    
                    // Find current active heading based on scroll position
                    if (headings.length > 0) {
                        let activeId = headings[0].id;
                        
                        for (let i = 0; i < headings.length; i++) {
                            const heading = headings[i];
                            const headingRect = heading.getBoundingClientRect();
                            const containerRect = scrollContainer.getBoundingClientRect();
                            const relativeTop = headingRect.top - containerRect.top;
                            
                            if (relativeTop < 100) {
                                activeId = heading.id;
                            } else {
                                break;
                            }
                        }
                        
                        // Only update if changed
                        if (activeId !== activeHeading) {
                            setActiveHeading(activeId);
                        }
                    }
                }
                
                // Attach scroll handler
                scrollContainer.onscroll = handleScroll;
                
                // Delay enabling the active scroll tracking to prevent cycling through headings on load
                setTimeout(() => {
                    scrollHandlerActive.current = true;
                    // Set initial state after delay
                    handleScroll();
                }, 2000);
            }
        }, 1500);
        
        // Cleanup function
        return () => {
            clearTimeout(setupTimer);
            if (contentRef.current) {
                contentRef.current.onscroll = null;
            }
            scrollHandlerActive.current = false;
        };
    }, [activeTab, processedTranscript?.content]); // Remove activeHeading to prevent cycles

    // Reset ToC initialization when content changes
    useEffect(() => {
        tocInitialized.current = false;
        scrollHandlerActive.current = false;
    }, [processedTranscript?.content]);

    // Update the markdown components
    const markdownComponents = {
        h1: ({ children }) => {
            const headingText = typeof children === 'string' ? children : children.join('');
            const id = headingText.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return (
                <h1
                    id={id}
                    className="text-3xl font-bold my-6 scroll-mt-32"
                    style={{ color: theme.text }}
                >
                    {children}
                </h1>
            );
        },
        h2: ({ children }) => {
            const headingText = typeof children === 'string' ? children : children.join('');
            const id = headingText.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return (
                <h2
                    id={id}
                    className="text-2xl font-bold my-4 scroll-mt-32"
                    style={{ color: theme.text }}
                >
                    {children}
                </h2>
            );
        },
        h3: ({ children }) => {
            const headingText = typeof children === 'string' ? children : children.join('');
            const id = headingText.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            return (
                <h3
                    id={id}
                    className="text-xl font-bold my-4 scroll-mt-32"
                    style={{ color: theme.text }}
                >
                    {children}
                </h3>
            );
        },
        p: ({ children }) => {
            // Check if this is a visualization placeholder
            if (typeof children === 'string' && children.startsWith(':::visualization')) {
                try {
                    const viz = JSON.parse(children.replace(/:::visualization(.*?):::/, '$1'));
                    if (viz.id) {
                        return <VisualizationRenderer key={viz.id} viz={viz} />;
                    }
                } catch (error) {
                    console.error('Error parsing visualization data:', error);
                    return (
                        <div className="my-4 p-4 rounded-lg" style={{ background: theme.error, color: '#FFFFFF' }}>
                            <p>Error rendering visualization</p>
                        </div>
                    );
                }
            }
            return (
                <p className="my-4 leading-relaxed" style={{ color: theme.text }}>
                    {children}
                </p>
            );
        },
        strong: ({ children }) => (
            <strong className="font-bold" style={{ color: theme.text }}>
                {children}
            </strong>
        ),
        a: ({ href, children }) => (
            <a href={href} className="text-blue-500 hover:underline">
                {children}
            </a>
        ),
        code: ({ inline, className, children }) => {
            if (inline) {
                return <code className="px-1 py-0.5 rounded bg-gray-100 text-gray-800">{children}</code>;
            }
            return (
                <pre className="p-4 rounded-lg bg-gray-100 overflow-x-auto">
                    <code className={className}>{children}</code>
                </pre>
            );
        },
        LectureVisualization: ({ viz }) => {
            if (!viz) {
                console.error('Visualization not provided');
                return (
                    <div className="my-6 p-4 rounded-lg" style={{ background: theme.error, color: '#FFFFFF' }}>
                        <p>Visualization not found</p>
                    </div>
                );
            }

            let vizData = viz.data;
            if (typeof vizData === 'string') {
                try {
                    vizData = JSON.parse(vizData);
                } catch (error) {
                    console.error('Error parsing visualization data:', error);
                    return (
                        <div className="my-6 p-4 rounded-lg" style={{ background: theme.error, color: '#FFFFFF' }}>
                            <p>Error parsing visualization data</p>
                        </div>
                    );
                }
            }

            console.log('Rendering visualization:', { id: viz.id, type: viz.type, data: vizData, description: viz.description });

            return (
                <div className="my-6">
                    <LectureVisualization
                        type={viz.type}
                        data={vizData}
                        description={viz.description}
                    />
                </div>
            );
        }
    };

    // Update the AI tools section render
    const renderAITool = () => {
        if (error) {
            return (
                <div className="mb-6 p-4 bg-red-50 border border-red-100 text-red-700 rounded-lg">
                    {error}
                </div>
            );
        }

        if (isLoading) {
            return (
                <div className="h-screen flex items-center justify-center">
                    <Loader percentage={loadingProgress} />
                </div>
            );
        }

        // Show a processing indicator when auto-processing is happening
        if (needsProcessing && isProcessing) {
            return (
                <div className="h-screen flex flex-col items-center justify-center" style={{ background: theme.background, color: theme.text }}>
                    <div className="text-center p-6 max-w-xl">
                        <div className="flex justify-center mb-4">
                            <Loader percentage={processingProgress} />
                        </div>
                        <h1 className="text-xl font-bold mb-4">Processing Lecture</h1>
                        <p className="mb-2">Your lecture is being processed. This usually takes 1-2 minutes.</p>
                        <p className="mt-2 text-sm">{processingProgress}% complete</p>
                    </div>
                </div>
            );
        }

        const currentToolGenerating = isGenerating[activeAITool?.replace('-', '')];

        if (currentToolGenerating) {
            return (
                <div className="h-[calc(100vh-200px)] flex justify-center items-center">
                    <div className="text-center">
                        <Loader percentage={processingProgress} />
                        <p className={`mt-4 ${theme.text}`}>
                            Generating {activeAITool?.replace('-', ' ')}...
                        </p>
                    </div>
                </div>
            );
        }

        switch (activeAITool) {
            case 'quiz':
                return quiz?.questions ? (
                    <Quiz
                        isDarkMode={theme.isDark}
                        questions={quiz.questions}
                        onSubmit={(score) => console.log('Quiz submitted:', score)}
                    />
                ) : null;

            case 'flashcards':
                return flashcards?.flashcards ? (
                    <Flashcards
                        isDarkMode={theme.isDark}
                        flashcards={flashcards.flashcards}
                    />
                ) : null;

            case 'concept-map':
                if (isGenerating.conceptMap) {
                    return (
                        <div className="h-[calc(100vh-200px)] flex justify-center items-center">
                            <div className="text-center">
                                <Loader percentage={processingProgress} />
                                <p className={`mt-4 ${theme.text}`}>
                                    Generating concept map...
                                </p>
                            </div>
                        </div>
                    );
                }

                if (generationError) {
                    return (
                        <div className="p-4 bg-red-50 text-red-700 rounded-lg">
                            <p>Error generating concept map: {generationError}</p>
                            <button
                                onClick={() => generateConceptMap()}
                                className="mt-2 px-4 py-2 bg-red-100 text-red-700 rounded hover:bg-red-200"
                            >
                                Retry
                            </button>
                        </div>
                    );
                }

                return conceptMap?.nodes ? (
                    <ConceptMap
                        isDarkMode={theme.isDark}
                        nodes={conceptMap.nodes}
                        edges={conceptMap.edges}
                    />
                ) : (
                    <div className="p-4 text-gray-500">
                        No concept map available.
                        <button
                            onClick={() => generateConceptMap()}
                            className="ml-2 text-blue-500 hover:underline"
                        >
                            Generate now
                        </button>
                    </div>
                );

            default:
                return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 sm:p-6">
                        <AIToolCard
                            title="Quiz"
                            description="Test your knowledge with AI-generated questions"
                            icon={<Brain size={24} />}
                            onClick={() => setActiveAITool('quiz')}
                            isDarkMode={theme.isDark}
                        />
                        <AIToolCard
                            title="Flashcards"
                            description="Review key concepts with flashcards"
                            icon={<Book size={24} />}
                            onClick={() => setActiveAITool('flashcards')}
                            isDarkMode={theme.isDark}
                        />
                        <AIToolCard
                            title="Concept Map"
                            description="Visualize relationships between concepts"
                            icon={<Network size={24} />}
                            onClick={() => setActiveAITool('concept-map')}
                            isDarkMode={theme.isDark}
                        />
                        <AIToolCard
                            title="Chat"
                            description="Ask questions about the lecture"
                            icon={<MessageSquare size={24} />}
                            onClick={() => navigate(`/class/${id}/lecture/${lectureId}/chat`)}
                            isDarkMode={theme.isDark}
                        />
                    </div>
                );
        }
    };

    const tabs = [
        { id: 'notes', label: 'Lecture Notes', icon: <FileText size={20} /> },
        { id: 'transcript', label: 'Raw Transcript', icon: <Book size={20} /> },
        {
            id: 'ai',
            label: 'AI Tools',
            icon: <Brain size={20} />,
            subTools: [
                { id: 'quiz', label: 'Quiz', icon: <Brain size={20} /> },
                { id: 'flashcards', label: 'Flashcards', icon: <BookOpen size={20} /> },
                { id: 'concept-map', label: 'Concept Map', icon: <Network size={20} /> },
                {
                    id: 'chat',
                    label: 'Chat',
                    icon: <MessageSquare size={20} />,
                    isLink: true,
                    onClick: () => navigate(`/class/${id}/lecture/${lectureId}/chat`)
                }
            ]
        }
    ];

    // Add useEffect to inject styles
    useEffect(() => {
        const styleElement = document.createElement('style');
        styleElement.textContent = latexStyles;
        document.head.appendChild(styleElement);

        return () => {
            document.head.removeChild(styleElement);
        };
    }, []);

    // Add window resize listener
    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640);
        };
        
        // Check initially
        checkMobile();
        
        // Add resize listener
        window.addEventListener('resize', checkMobile);
        
        // Cleanup
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    // Update icon rendering in tabs
    const renderIcon = (icon) => {
        return React.cloneElement(icon, { size: isMobile ? 16 : 20 });
    };

    // Add authentication loading state
    if (!isLoaded) {
        return <Loader />;
    }

    // Add loading state check
    if (isLoading) {
        return (
            <div className="h-screen flex items-center justify-center">
                <Loader percentage={loadingProgress} />
            </div>
        );
    }

    // Add error state check
    if (error) {
        return (
            <div className="p-4 bg-red-50 text-red-700 rounded-lg">
                <h2>Error</h2>
                <p>{error}</p>
            </div>
        );
    }

    // Add null check for lecture
    if (!lecture) {
        return (
            <div className="p-4 text-gray-500">
                No lecture found
            </div>
        );
    }

    const handleShare = () => {
        setShowShareModal(true);
    };

    const handleExportPDF = () => {
        setShowExportModal(true);
    };

    const handleExport = async (exportOptions) => {
        if (!processedTranscript?.content) {
            toast.error('No content available to export');
            return;
        }

        try {
            const content = processContent(
                processedTranscript.content,
                processedTranscript.visualizations
            );

            await exportToPDF(
                content, 
                lecture.title, 
                exportOptions, 
                toast
            );

            setShowExportModal(false);
        } catch (error) {
            console.error('Error exporting document:', error);
            toast.error(`Export failed: ${error.message}`);
        }
    };

    // Restore the essential functions
    const handleScrollToSection = (headingId) => {
        const element = document.getElementById(headingId);
        if (element) {
            setActiveHeading(headingId);
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    // Replace the processContent function with this new version
    const processContent = (content, visualizations) => {
        if (!content) return '';
        if (!visualizations || !Array.isArray(visualizations)) return content;
        
        // Normalization helper: remove spaces, underscores, and hyphens; use lowercase.
        const normalizeId = (id) => id.toLowerCase().replace(/[\s_-]/g, '');

        // Create a map of visualizations using normalized IDs
        const vizMap = new Map(visualizations.map(viz => [normalizeId(viz.id), viz]));
        
        // Replace viz_placeholder tags with our custom marker
        let processedContent = content.replace(
            /<viz_placeholder[^>]*id="([^"]*)"[^>]*>[\s\S]*?<\/viz_placeholder>/g,
            (match, id) => {
                const normalizedId = normalizeId(id);
                let viz = vizMap.get(normalizedId);

                // Attempt fuzzy matching if no exact match is found.
                if (!viz) {
                    const candidateKey = Array.from(vizMap.keys()).find(key =>
                        key.includes(normalizedId) || normalizedId.includes(key)
                    );
                    if (candidateKey) {
                        console.warn(`No exact visualization found for id: ${id}, using candidate with key: ${candidateKey}`);
                        viz = vizMap.get(candidateKey);
                    } else {
                        console.warn(`No visualization found for id: ${id}`);
                        return '';
                    }
                }

                return `\n\n:::visualization${JSON.stringify(viz)}:::\n\n`;
            }
        );

        // LATEX RENDERING ENHANCEMENT
        
        // 1. First, protect dollar-sign amounts (like $50) by replacing them temporarily
        processedContent = processedContent.replace(/\$(\d+(\.\d+)?)/g, '___DOLLAR___$1');
        
        // 2. Fix any remaining double backslashes in LaTeX commands
        
        // Replace excessive backslashes in common LaTeX commands
        processedContent = processedContent.replace(/\\\\([a-zA-Z]+)/g, '\\$1');
        
        // Common LaTeX commands that might need fixing
        const commonCommands = ['alpha', 'beta', 'gamma', 'delta', 'epsilon', 'theta', 'lambda', 'pi', 'sigma', 
                               'frac', 'sqrt', 'int', 'sum', 'prod', 'lim', 'inf', 'partial', 'nabla', 
                               'times', 'div', 'cdot', 'equiv', 'approx', 'neq', 'leq', 'geq'];
        
        // Fix double backslashes before common commands
        commonCommands.forEach(cmd => {
            const regExp = new RegExp(`\\\\+${cmd}`, 'g');
            processedContent = processedContent.replace(regExp, `\\${cmd}`);
        });
        
        // 3. Handle block equations ($$...$$) - ensure they have newlines around them
        processedContent = processedContent.replace(/\$\$([\s\S]*?)\$\$/g, (match, equation) => {
            // Clean up equation content - replace multiple backslashes
            const cleanEquation = equation.trim()
                .replace(/\\{3,}/g, '\\')  // Replace 3+ backslashes with one
                .replace(/\\{2}/g, '\\')   // Replace 2 backslashes with one
                .replace(/\n\s*/g, ' ');   // Clean up newlines and spaces
            
            // Add extra space around display equations for better rendering
            return `\n\n$$${cleanEquation}$$\n\n`;
        });

        // 4. Fix inline math ($...$)
        // Ensure inline math ($...$) is properly spaced from text
        processedContent = processedContent.replace(/(\S)(\$[^\$\n]+?\$)(\S)/g, '$1 $2 $3');
        
        // Fix common LaTeX issues in inline math
        processedContent = processedContent.replace(/\$([^\$\n]+?)\$/g, (match, equation) => {
            // Clean equation by removing excess whitespace and fix backslashes
            const cleanEquation = equation.trim()
                .replace(/\\{3,}/g, '\\')  // Replace 3+ backslashes with one
                .replace(/\\{2}/g, '\\')   // Replace 2 backslashes with one
                .replace(/\s+/g, ' ');     // Normalize spaces
            
            return `$${cleanEquation}$`;
        });
        
        // 5. Restore protected dollar amounts
        processedContent = processedContent.replace(/___DOLLAR___(\d+(\.\d+)?)/g, '\\$$$1');

        // 6. Ensure code blocks have proper spacing
        processedContent = processedContent.replace(/```([\s\S]*?)```/g, (match, code) => {
            // Add extra line breaks around code blocks
            return `\n\n\`\`\`${code}\`\`\`\n\n`;
        });

        // 7. Ensure headings have proper spacing for page breaks
        processedContent = processedContent.replace(/\n(#{1,3}\s.*)\n/g, "\n\n$1\n\n");
        
        // 8. Double-check that no equations are directly adjacent to text without spaces
        processedContent = processedContent.replace(/(\w)(\$\$)/g, '$1 $2');
        processedContent = processedContent.replace(/(\$\$)(\w)/g, '$1 $2');
        processedContent = processedContent.replace(/(\w)(\$[^\$]+\$)(\w)/g, '$1 $2 $3');

        return processedContent;
    };

    // Add this helper function to safely parse JSON
    const safeJSONParse = (str) => {
        if (!str) return null;
        if (typeof str === 'object') return str;

        try {
            // First try direct parsing
            return JSON.parse(str);
        } catch (e) {
            try {
                // If that fails, try cleaning the string first
                const cleaned = str
                    .replace(/'/g, '"')  // Replace single quotes with double quotes
                    .replace(/([{,]\s*)(\w+):/g, '$1"$2":')  // Quote unquoted property names
                    .replace(/\\n/g, '\\n')
                    .replace(/\\'/g, "\\'")
                    .replace(/\\"/g, '\\"')
                    .replace(/\\&/g, '\\&')
                    .replace(/\\r/g, '\\r')
                    .replace(/\\t/g, '\\t')
                    .replace(/\\b/g, '\\b')
                    .replace(/\\f/g, '\\f');
                return JSON.parse(cleaned);
            } catch (e2) {
                console.error('Error parsing JSON after cleaning:', e2);
                return null;
            }
        }
    };

    // Update the VisualizationRenderer component to accept the full visualization object
    const VisualizationRenderer = ({ viz }) => {
        if (!viz) {
            console.error('Visualization not provided');
            return (
                <div className="my-6 p-4 rounded-lg" style={{ background: theme.error, color: '#FFFFFF' }}>
                    <p>Visualization not found</p>
                </div>
            );
        }

        let vizData = viz.data;
        if (typeof vizData === 'string') {
            try {
                vizData = JSON.parse(vizData);
            } catch (error) {
                console.error('Error parsing visualization data:', error);
                return (
                    <div className="my-6 p-4 rounded-lg" style={{ background: theme.error, color: '#FFFFFF' }}>
                        <p>Error parsing visualization data</p>
                    </div>
                );
            }
        }

        console.log('Rendering visualization:', { id: viz.id, type: viz.type, data: vizData, description: viz.description });

        return (
            <div className="my-6">
                <LectureVisualization
                    type={viz.type}
                    data={vizData}
                    description={viz.description}
                />
            </div>
        );
    };

    return (
        <div className="h-screen overflow-hidden flex flex-col" style={{ background: theme.background, color: theme.text }}>
            <header className="flex-shrink-0 h-[120px] sm:h-[160px]" style={{ borderBottom: `1px solid ${theme.border}` }}>
                <div className="h-full px-2 mt-2 sm:px-6 sm:mt-4 lg:px-8">
                    <nav className="flex items-center text-xs sm:text-sm mt-2 sm:mt-4">
                        <button
                            onClick={() => navigate('/classes')}
                            style={{ color: theme.accent }}
                            className="hover:underline flex-shrink-0"
                        >
                            Classes
                        </button>
                        <span style={{ color: theme.accent }} className="mx-1 sm:mx-2 flex-shrink-0">/</span>
                        <button
                            onClick={() => navigate(`/class/${id}/lectures`)}
                            style={{ color: theme.accent }}
                            className="hover:underline flex-shrink-0"
                        >
                            {className || lecture?.className || '...'}
                        </button>
                        <span style={{ color: theme.accent }} className="mx-1 sm:mx-2 flex-shrink-0">/</span>
                        <span style={{ color: theme.text }} className="truncate max-w-[100px] sm:max-w-[300px] md:max-w-[400px] flex-shrink inline-block">
                            {lecture?.title || '...'}
                        </span>
                    </nav>

                    <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-4 mt-2 sm:mt-4">
                        <h1 className="text-xl sm:text-2xl font-bold truncate max-w-full sm:max-w-[50%] flex-shrink" style={{ color: theme.text }}>
                            {lecture?.title || 'Loading...'}
                        </h1>
                        <div className="flex items-center gap-1 sm:gap-2 ml-0 sm:ml-auto flex-shrink-0 flex-wrap sm:flex-nowrap">
                            <button
                                onClick={() => setShowShareModal(true)}
                                className="px-2 sm:px-4 py-1 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 hover:scale-[1.02] transform transition-all duration-200 text-xs sm:text-base"
                                style={{ background: theme.primary, color: theme.text }}
                            >
                                <Share2 size={16} className="sm:size-[18px]" />
                                <span className="whitespace-nowrap">Share</span>
                            </button>
                            <button
                                onClick={handleExportPDF}
                                className="px-2 sm:px-4 py-1 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 hover:scale-[1.02] transform transition-all duration-200 text-xs sm:text-base"
                                style={{ background: theme.primary, color: theme.text }}
                            >
                                <Download size={16} className="sm:size-[18px]" />
                                <span className="whitespace-nowrap">Export PDF</span>
                            </button>
                            <button
                                onClick={handleRegenerate}
                                disabled={isProcessing}
                                className={`px-2 sm:px-4 py-1 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 transform transition-all duration-200 text-xs sm:text-base ${
                                    isProcessing ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.02]'
                                }`}
                                style={{ 
                                    background: isProcessing ? theme.border : theme.primary,
                                    color: theme.text
                                }}
                            >
                                <RotateCw className={`${isProcessing ? 'animate-spin' : ''} size-4 sm:size-[18px]`} />
                                <span className="whitespace-nowrap">{isProcessing ? 'Processing...' : 'Regenerate Notes'}</span>
                            </button>
                        </div>
                    </div>

                    <div className="flex items-center space-x-1 sm:space-x-2 mt-2 sm:mt-4 overflow-x-auto pb-2 sm:pb-0">
                        {tabs.map(tab => (
                            <div key={tab.id} className={tab.id === 'ai' ? 'ai-tools-container' : ''}>
                                <button
                                    onClick={() => {
                                        setActiveTab(tab.id);
                                        if (tab.id === 'ai' && !activeAITool) {
                                            setActiveAITool('quiz');
                                        }
                                    }}
                                    className="px-2 sm:px-4 py-1 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 transition-all min-w-[90px] sm:min-w-[120px] whitespace-nowrap text-xs sm:text-base"
                                    style={{
                                        background: activeTab === tab.id ? theme.primary : theme.secondary,
                                        color: theme.text
                                    }}
                                >
                                    {renderIcon(tab.icon)}
                                    <span>{tab.label}</span>
                                </button>
                                
                                {tab.id === 'ai' && (
                                    <div className="sub-tools">
                                        {tab.subTools.map((subTool) => (
                                            <button
                                                key={subTool.id}
                                                onClick={() => {
                                                    if (subTool.isLink && subTool.onClick) {
                                                        subTool.onClick();
                                                    } else {
                                                        setActiveTab('ai');
                                                        setActiveAITool(subTool.id);
                                                    }
                                                }}
                                                className="sub-tool px-2 sm:px-4 py-1 sm:py-2 rounded-lg flex items-center gap-1 sm:gap-2 min-w-[90px] sm:min-w-[120px] shadow-sm whitespace-nowrap text-xs sm:text-base"
                                                style={{
                                                    background: activeTab === 'ai' && activeAITool === subTool.id && !subTool.isLink
                                                        ? theme.primary
                                                        : theme.secondary,
                                                    color: theme.text
                                                }}
                                            >
                                                {renderIcon(subTool.icon)}
                                                <span>{subTool.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </header>
            
            <main className="flex-1 min-h-0">
                <div className="h-full px-2 sm:px-6 lg:px-8">
                    {error ? (
                        <div className="rounded-lg p-3 sm:p-4 m-2 sm:m-4" style={{ background: theme.error, color: '#FFFFFF' }}>
                            <p className="font-medium">Error</p>
                            <p className="text-sm">{error}</p>
                        </div>
                    ) : !lecture || !processedTranscript ? (
                        <div className="flex justify-center items-center py-20">
                            <Loader />
                        </div>
                    ) : (
                        <div className="h-full rounded-lg mt-2 sm:mt-4" style={{ background: theme.background }}>
                            {activeTab === 'notes' && (
                                <>
                                    <div className="h-[calc(100vh-180px)] md:h-[calc(100vh-240px)] block md:grid md:grid-cols-4 gap-6">
                                        {isLoading ? (
                                            <div className="col-span-4 flex justify-center items-center">
                                                <Loader />
                                            </div>
                                        ) : error ? (
                                            <div className="col-span-4 p-4" style={{ background: theme.error, color: '#FFFFFF', borderRadius: '0.5rem' }}>
                                                <p>{error}</p>
                                            </div>
                                        ) : !processedTranscript ? (
                                            <div className="col-span-4 p-4" style={{ color: theme.accent }}>
                                                No processed transcript available
                                            </div>
                                        ) : (
                                            <>
                                                {/* Mobile ToC toggle - Update the styling */}
                                                <div className="md:hidden mb-2 px-2">
                                                    <button 
                                                        onClick={() => setMobileToC(prev => !prev)}
                                                        className="w-full flex items-center justify-between p-2 sm:p-3 rounded-lg text-sm"
                                                        style={{
                                                            background: theme.secondary,
                                                            border: `1px solid ${theme.border}`
                                                        }}
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <Book size={16} style={{ color: theme.accent }} />
                                                            <span style={{ color: theme.text }}>Table of Contents</span>
                                                        </div>
                                                        {mobileToC ? 
                                                            <ChevronUp size={16} style={{ color: theme.accent }} /> : 
                                                            <ChevronDown size={16} style={{ color: theme.accent }} />
                                                        }
                                                    </button>
                                                </div>

                                                {/* Table of Contents - Hidden by default on mobile, toggleable */}
                                                <div className={`${mobileToC ? 'block' : 'hidden'} md:block md:col-span-1 overflow-y-auto h-full mb-4 md:mb-0`}>
                                                    <div
                                                        ref={tocRef}
                                                        className="md:sticky md:top-0 overflow-y-auto mt-0 md:mt-4"
                                                    >
                                                        <div className="p-3 sm:p-4 rounded-lg" style={{
                                                            background: theme.secondary,
                                                            border: `1px solid ${theme.border}`
                                                        }}>
                                                            <h3 className="font-medium mb-2 sm:mb-3" style={{ color: theme.text }}>
                                                                Contents
                                                            </h3>

                                                            <div className="relative w-full h-1 mb-3 sm:mb-4 rounded-full overflow-hidden" style={{ background: theme.border }}>
                                                                <div
                                                                    className="absolute h-full transition-all duration-300 ease-out"
                                                                    style={{
                                                                        width: `${readingProgress}%`,
                                                                        background: theme.primary
                                                                    }}
                                                                />
                                                            </div>

                                                            <nav className="space-y-1 sm:space-y-2">
                                                                {processedTranscript?.content?.match(/#{1,3}.+/g)?.map((heading, index) => {
                                                                    const headingText = heading.replace(/#/g, '').trim();
                                                                    const headingId = headingText.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                                                                    const headingLevel = (heading.match(/#/g) || []).length;
                                                                    const isActive = activeHeading === headingId;

                                                                    return (
                                                                        <button
                                                                            key={index}
                                                                            onClick={() => {
                                                                                handleScrollToSection(headingId);
                                                                                if (window.innerWidth < 768) {
                                                                                    setMobileToC(false);
                                                                                }
                                                                            }}
                                                                            className={`
                                                                                block text-left w-full text-xs sm:text-sm transition-all duration-200 py-1 my-0.5 sm:my-1 rounded
                                                                                ${headingLevel === 3 ? 'pl-4' : ''}
                                                                                ${headingLevel === 2 ? 'pl-2' : ''}
                                                                                ${isActive ? 'bg-opacity-10 transform scale-[1.02]' : 'hover:bg-opacity-5'}
                                                                            `}
                                                                            style={{
                                                                                color: isActive ? theme.text : theme.accent,
                                                                                fontWeight: isActive ? '600' : 'normal',
                                                                                backgroundColor: isActive ? theme.primary : 'transparent',
                                                                                borderLeft: isActive ? `3px solid ${theme.primary}` : 'none',
                                                                                paddingLeft: isActive ? (headingLevel === 3 ? '1.1rem' : (headingLevel === 2 ? '0.6rem' : '0.2rem')) : undefined
                                                                            }}
                                                                        >
                                                                            {headingText}
                                                                        </button>
                                                                    );
                                                                })}
                                                            </nav>
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Content Area */}
                                                <div className="md:col-span-3 h-full">
                                                    <div
                                                        ref={contentRef}
                                                        className="h-full rounded-lg p-3 sm:p-6 overflow-y-auto my-2 sm:my-4 scroll-container"
                                                        style={{
                                                            background: theme.secondary,
                                                            border: `1px solid ${theme.border}`,
                                                            overflowY: 'auto',
                                                            height: 'calc(100vh - 230px)',
                                                            position: 'relative',
                                                            scrollBehavior: 'smooth'
                                                        }}
                                                    >
                                                        <div ref={proseRef} className="prose max-w-none text-sm sm:text-base" style={{ color: theme.text }}>
                                                            <ReactMarkdown
                                                                remarkPlugins={[remarkMath, remarkGfm]}
                                                                rehypePlugins={[rehypeKatex, rehypeRaw]}
                                                                components={{
                                                                    ...markdownComponents,
                                                                    h1: ({ children }) => {
                                                                        const headingText = typeof children === 'string' ? children : children.join('');
                                                                        const id = headingText.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                                                                        return (
                                                                            <h1
                                                                                id={id}
                                                                                className="text-2xl sm:text-3xl font-bold my-4 sm:my-6 scroll-mt-32"
                                                                                style={{ color: theme.text }}
                                                                            >
                                                                                {children}
                                                                            </h1>
                                                                        );
                                                                    },
                                                                    h2: ({ children }) => {
                                                                        const headingText = typeof children === 'string' ? children : children.join('');
                                                                        const id = headingText.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                                                                        return (
                                                                            <h2
                                                                                id={id}
                                                                                className="text-xl sm:text-2xl font-bold my-3 sm:my-4 scroll-mt-32"
                                                                                style={{ color: theme.text }}
                                                                            >
                                                                                {children}
                                                                            </h2>
                                                                        );
                                                                    },
                                                                    h3: ({ children }) => {
                                                                        const headingText = typeof children === 'string' ? children : children.join('');
                                                                        const id = headingText.toLowerCase().replace(/[^a-z0-9]+/g, '-');
                                                                        return (
                                                                            <h3
                                                                                id={id}
                                                                                className="text-lg sm:text-xl font-bold my-3 sm:my-4 scroll-mt-32"
                                                                                style={{ color: theme.text }}
                                                                            >
                                                                                {children}
                                                                            </h3>
                                                                        );
                                                                    }
                                                                }}
                                                            >
                                                                {processedTranscript?.content ?
                                                                    processContent(
                                                                        processedTranscript.content,
                                                                        processedTranscript.visualizations
                                                                    ) : ''
                                                                }
                                                            </ReactMarkdown>
                                                        </div>
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}

                            {activeTab === 'transcript' && (
                                <>
                                    <div className="h-full overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                                        {isLoading ? (
                                            <div className="flex justify-center items-center h-full">
                                                <Loader />
                                            </div>
                                        ) : error ? (
                                            <div className="p-4 rounded-lg" style={{ background: theme.error, color: '#FFFFFF' }}>
                                                <p>{error}</p>
                                            </div>
                                        ) : !lecture?.transcript ? (
                                            <div className="p-4" style={{ color: theme.accent }}>
                                                No transcript available
                                            </div>
                                        ) : (
                                            <>
                                                <div className="mb-6 p-4 rounded-lg flex items-start gap-3" style={{
                                                    background: theme.secondary,
                                                    color: theme.text,
                                                    border: `1px solid ${theme.border}`
                                                }}>
                                                    <FileText size={20} style={{ color: theme.primary }} className="mt-1 flex-shrink-0" />
                                                    <div>
                                                        <h3 className="font-medium mb-1" style={{ color: theme.text }}>Raw Transcript</h3>
                                                        <p className="text-sm" style={{ color: theme.accent }}>This is the unprocessed transcript of your lecture. Use the tools below to search and annotate.</p>
                                                    </div>
                                                </div>

                                                <div className="rounded-lg overflow-hidden" style={{
                                                    background: theme.secondary,
                                                    border: `1px solid ${theme.border}`
                                                }}>
                                                    <div className="sticky top-0 z-20 bg-inherit" style={{ borderBottom: `1px solid ${theme.border}` }}>
                                                        <div className="p-3 sm:p-4" style={{ borderBottom: `1px solid ${theme.border}` }}>
                                                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg" style={{
                                                                background: theme.background,
                                                                border: `1px solid ${theme.border}`
                                                            }}>
                                                                <Search size={18} style={{ color: theme.accent }} />
                                                                <input
                                                                    type="text"
                                                                    placeholder="Search in transcript..."
                                                                    value={searchQuery}
                                                                    onChange={handleSearch}
                                                                    className="flex-grow bg-transparent outline-none placeholder:text-gray-400 w-full"
                                                                    style={{ color: theme.text }}
                                                                />
                                                            </div>
                                                        </div>

                                                        <div className="px-2 py-2 flex flex-wrap items-center gap-1 sm:gap-2">
                                                            <button
                                                                onClick={handleHighlight}
                                                                disabled={!selectedText}
                                                                className="p-2 rounded-lg flex items-center justify-center sm:gap-2 transition-colors"
                                                                style={{
                                                                    color: theme.text,
                                                                    opacity: !selectedText ? 0.5 : 1,
                                                                    cursor: !selectedText ? 'not-allowed' : 'pointer'
                                                                }}
                                                                title="Highlight"
                                                            >
                                                                <Highlighter size={18} />
                                                                <span className="hidden sm:inline">Highlight</span>
                                                            </button>
                                                            <button
                                                                onClick={handleUnderline}
                                                                disabled={!selectedText}
                                                                className="p-2 rounded-lg flex items-center justify-center sm:gap-2 transition-colors"
                                                                style={{
                                                                    color: theme.text,
                                                                    opacity: !selectedText ? 0.5 : 1,
                                                                    cursor: !selectedText ? 'not-allowed' : 'pointer'
                                                                }}
                                                                title="Underline"
                                                            >
                                                                <Underline size={18} />
                                                                <span className="hidden sm:inline">Underline</span>
                                                            </button>
                                                            <button
                                                                onClick={handleCopy}
                                                                disabled={!selectedText}
                                                                className="p-2 rounded-lg flex items-center justify-center sm:gap-2 transition-colors"
                                                                style={{
                                                                    color: theme.text,
                                                                    opacity: !selectedText ? 0.5 : 1,
                                                                    cursor: !selectedText ? 'not-allowed' : 'pointer'
                                                                }}
                                                                title="Copy"
                                                            >
                                                                <Copy size={18} />
                                                                <span className="hidden sm:inline">Copy</span>
                                                            </button>
                                                            <button
                                                                onClick={handleBookmark}
                                                                disabled={!selectedText}
                                                                className="p-2 rounded-lg flex items-center justify-center sm:gap-2 transition-colors"
                                                                style={{
                                                                    color: theme.text,
                                                                    opacity: !selectedText ? 0.5 : 1,
                                                                    cursor: !selectedText ? 'not-allowed' : 'pointer'
                                                                }}
                                                                title="Bookmark"
                                                            >
                                                                <BookmarkPlus size={18} />
                                                                <span className="hidden sm:inline">Bookmark</span>
                                                            </button>
                                                            <button
                                                                onClick={handleShare}
                                                                className="p-2 rounded-lg flex items-center justify-center sm:gap-2 transition-colors"
                                                                style={{ color: theme.text }}
                                                                title="Share"
                                                            >
                                                                <Share2 size={18} />
                                                                <span className="hidden sm:inline">Share</span>
                                                            </button>
                                                            <button
                                                                onClick={handleExportPDF}
                                                                className="p-2 rounded-lg flex items-center justify-center sm:gap-2 transition-colors"
                                                                style={{ color: theme.text }}
                                                                title="Export PDF"
                                                            >
                                                                <Download size={18} />
                                                                <span className="hidden sm:inline">Export</span>
                                                            </button>
                                                            <div className="hidden sm:block h-6 w-px mx-2" style={{ background: theme.border }} />
                                                            <span className="text-xs sm:text-sm ml-1 sm:ml-0" style={{ color: theme.accent }}>
                                                                {selectedText ? 'Text selected' : 'Select text to use tools'}
                                                            </span>
                                                        </div>
                                                    </div>

                                                    <div
                                                        className="divide-y"
                                                        style={{ borderColor: theme.border }}
                                                        onMouseUp={handleTextSelection}
                                                        onTouchEnd={handleTextSelection}
                                                    >
                                                        {lecture.transcript.split('\n').map((line, index) => {
                                                            const isTimestamp = /^\[\d{2}:\d{2}:\d{2}\]/.test(line);
                                                            const isSpeaker = /^[A-Za-z\s]+:/.test(line);

                                                            if (line.trim() === '') return null;

                                                            // Function to highlight searched text
                                                            const highlightSearchText = (text) => {
                                                                if (!searchQuery.trim()) return text;

                                                                const parts = text.split(new RegExp(`(${searchQuery})`, 'gi'));
                                                                return parts.map((part, i) =>
                                                                    part.toLowerCase() === searchQuery.toLowerCase()
                                                                        ? <mark key={i} className="bg-yellow-200 text-gray-900">{part}</mark>
                                                                        : part
                                                                );
                                                            };

                                                            // Function to apply highlights and underlines
                                                            const applyTextStyles = (text) => {
                                                                let styledText = text;

                                                                // Apply highlights
                                                                highlights.forEach(highlight => {
                                                                    if (text.includes(highlight)) {
                                                                        styledText = text.split(highlight).map((part, i, arr) =>
                                                                            i < arr.length - 1
                                                                                ? <React.Fragment key={i}>{part}<span className="bg-yellow-200">{highlight}</span></React.Fragment>
                                                                                : part
                                                                        );
                                                                    }
                                                                });

                                                                // Apply underlines
                                                                underlines.forEach(underline => {
                                                                    if (text.includes(underline)) {
                                                                        styledText = text.split(underline).map((part, i, arr) =>
                                                                            i < arr.length - 1
                                                                                ? <React.Fragment key={i}>{part}<span className="underline decoration-2">{underline}</span></React.Fragment>
                                                                                : part
                                                                        );
                                                                    }
                                                                });

                                                                return styledText;
                                                            };

                                                            return (
                                                                <div
                                                                    key={index}
                                                                    className={`relative ${isTimestamp ? 'sticky top-[97px] sm:top-[57px] z-10' : ''}`}
                                                                >
                                                                    {isTimestamp ? (
                                                                        <div className="flex items-center gap-3 p-2 sm:p-3 backdrop-blur-sm" style={{
                                                                            background: `${theme.background}95`
                                                                        }}>
                                                                            <span className="px-2 py-1 rounded text-xs font-medium" style={{
                                                                                background: theme.primary,
                                                                                color: theme.text
                                                                            }}>
                                                                                {line.replace(/[\[\]]/g, '')}
                                                                            </span>
                                                                            <div className="h-[1px] flex-grow" style={{ background: theme.border }}></div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="p-2 sm:p-3">
                                                                            {isSpeaker ? (
                                                                                <div className="flex items-center gap-2 sm:gap-3 mb-2">
                                                                                    <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full flex items-center justify-center" style={{
                                                                                        background: theme.primary
                                                                                    }}>
                                                                                        <span className="text-sm font-medium" style={{ color: theme.text }}>
                                                                                            {line.split(':')[0].charAt(0)}
                                                                                        </span>
                                                                                    </div>
                                                                                    <span className="font-medium" style={{ color: theme.text }}>
                                                                                        {line.split(':')[0]}
                                                                                    </span>
                                                                                </div>
                                                                            ) : (
                                                                                <p className="pl-9 sm:pl-11 text-sm sm:text-base leading-relaxed" style={{ color: theme.text }}>
                                                                                    {applyTextStyles(highlightSearchText(line))}
                                                                                </p>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </>
                            )}

                            {activeTab === 'ai' && (
                                <div className="h-full overflow-y-auto" style={{ maxHeight: 'calc(100vh - 200px)' }}>
                                    {renderAITool()}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </main>

            <ExportOptionsModal
                isOpen={showExportModal}
                onClose={() => setShowExportModal(false)}
                onExport={handleExport}
            />

            <ShareModal
                isOpen={showShareModal}
                onClose={() => setShowShareModal(false)}
                classId={id}
                className={className}
                lectureId={lectureId}
                lectureName={lecture?.title}
            />
        </div>
    );
};

const AIToolCard = ({ title, description, icon, onClick }) => {
    const { theme } = useTheme();
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 640);
        };
        
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
        <button
            onClick={onClick}
            className="group p-3 sm:p-6 rounded-xl transition-all flex items-start gap-3 sm:gap-4 border-2 border-transparent hover:border-sand w-full touch-manipulation"
            style={{
                background: theme.secondary,
                color: theme.text
            }}
        >
            <div className="text-sand p-2 sm:p-3 rounded-lg transition-colors flex-shrink-0" style={{ background: theme.primary }}>
                {React.cloneElement(icon, { size: isMobile ? 16 : 24 })}
            </div>
            <div className="text-left">
                <h3 className="font-semibold text-sm sm:text-lg mb-1" style={{ color: theme.text }}>
                    {title}
                </h3>
                <p className="text-xs sm:text-sm" style={{ color: theme.accent }}>
                    {description}
                </p>
            </div>
        </button>
    );
};

export default LectureDetail;